import React, { useMemo } from 'react';

import { SmBadge, SmButton } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';

import useApi from '@/hooks/useApi';
import useEvent from '@/hooks/useEvent';

import { TVoucherItem } from '@/services/requests/Voucher/types';
import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { dateStringFormat } from '@/helpers/StringHelper/dateStringFormat';

import { TScreens } from '../..';

type TProps = {
  cardData: TVoucherItem;
  liferayContent: TModalContent;
  setChangeVoucher: (voucherNumber: string) => void;
  changeScreen: (newScreen: TScreens) => void;
};

export function VoucherCard({
  cardData,
  liferayContent,
  setChangeVoucher,
  changeScreen,
}: TProps) {
  const percent =
    cardData.totalUsed && cardData.quantity
      ? (+cardData.totalUsed / +cardData.quantity) * 100
      : 0;

  const { ApiInteractions } = useApi();
  const event = useEvent();

  const handleClickVoucher = (): void => {
    dispatchInteraction(
      ApiInteractions,
      'extra_mpv_select_voucher_click_voucher',
      {
        recommendationId: event.data.recommendationId,
        details: { voucherNumber: cardData.voucherNumber },
      },
    );
    setChangeVoucher(cardData.voucherNumber);
    changeScreen('dashboard');
  };

  const liferayFilled = useMemo<TModalContent>(() => {
    return JSON.parse(
      Mustache.render(JSON.stringify(liferayContent), cardData),
    );
  }, [liferayContent, cardData]);
  return (
    <div className="smls-b2c-modals__vouchercard">
      <div className="smls-b2c-modals__vouchercard_title">
        <div className="title-icon">
          <img src={liferayFilled.voucherIcon?.icon} alt="" />
        </div>
        <span className="title-text">
          {liferayFilled.voucherTitle?.text && (
            <span
              className="text" // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: liferayFilled.voucherTitle.text,
              }}
            />
          )}{' '}
          {cardData.reusable && liferayFilled.reusableLabel?.text && (
            <SmBadge
              text={clearHtmlTags(liferayFilled.reusableLabel.text)}
              variant="primary"
            />
          )}
        </span>
      </div>
      <div className="smls-b2c-modals__vouchercard__middle">
        {cardData.reusable ? (
          <>
            <div className="progress">
              <div style={{ width: `${percent}%` }} />
            </div>
            <div className="text">
              <strong>
                {cardData.totalUsed
                  .toString()
                  .replace(/([\d]+)([\d]{3})/, '$1.$2')}
              </strong>{' '}
              {clearHtmlTags(liferayFilled.ofLabel?.text || '').concat(' ')}
              <strong>
                {cardData.quantity
                  .toString()
                  .replace(/([\d]+)([\d]{3})/, '$1.$2')}
              </strong>{' '}
              {clearHtmlTags(liferayFilled.milesLabel?.text || '')}
            </div>
          </>
        ) : (
          <div className="empty-middle" />
        )}
      </div>
      <div className="smls-b2c-modals__vouchercard__cta-line">
        <div className="smls-b2c-modals__vouchercard__validate">
          <div className="smls-b2c-modals__vouchercard__validate icon">
            <img src={liferayFilled.voucherValidity?.icon} alt="" />
          </div>
          {liferayFilled.voucherValidity?.text && (
            <span
              className="smls-b2c-modals__vouchercard__validate date_text" // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: dateStringFormat(liferayFilled.voucherValidity.text),
              }}
            />
          )}
        </div>
        <div className="smls-b2c-modals__vouchercard__botton">
          <SmButton
            type="button"
            text={clearHtmlTags(liferayFilled.voucherUseBtn?.text || '')}
            onClick={handleClickVoucher}
          />
        </div>
        <div className="smls-b2c-modals__vouchercard__botton__mobile">
          <SmButton
            isPill
            isOutlined
            icon={<span className="material-icons">arrow_forward_ios</span>}
            onClick={handleClickVoucher}
          />
        </div>
      </div>
    </div>
  );
}
